import { httpClient } from '@/httpClient/axios.ts';
import { useAppConfig } from '@/appConfig/useAppConfig.ts';
import { useState } from 'react';
import Cookies from 'js-cookie';
import * as Sentry from '@sentry/browser';
import {
  AddressLookup,
  AddressLookupStatus,
  AddressSearch,
  AddressSearchStatus,
  LookupAddressResponse,
  SearchAddressResponse,
} from '@/searchAddress/AddressTypes.ts';

export const useSearchAddressActions = () => {
  const meta = useAppConfig();
  const SPINNER_DURATION = 1000;

  const normalizeCountry = (country?: string) => country?.trim().toLowerCase();

  const normalizeSearchParams = (addressSearch: AddressSearch): AddressSearch => ({
    ...addressSearch,
    query: addressSearch.query.trim(),
    country: normalizeCountry(addressSearch.country),
  });

  const normalizeLookupParams = (addressLookup: AddressLookup): AddressLookup => ({
    ...addressLookup,
    location_id: addressLookup.location_id.trim(),
    country: normalizeCountry(addressLookup.country),
  });

  const [searchStatus, setSearchStatus] = useState<AddressSearchStatus>('unknown');
  const [lookupStatus, setLookupStatus] = useState<AddressLookupStatus>('unknown');

  const searchAddress = async (addressSearch: AddressSearch) => {
    if (!meta) {
      return Promise.reject(new Error('Missing app config'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'X-CSRFToken': Cookies.get('csrftoken'),
    };

    setSearchStatus('sending');

    try {
      const params = normalizeSearchParams(addressSearch);
      const response = await httpClient.get<SearchAddressResponse>(meta.api.searchAddress, {
        headers,
        params,
      });

      await new Promise((res) => setTimeout(res, SPINNER_DURATION));
      setSearchStatus('done');

      // Backend: { results: [...] }
      return response.data.results;
    } catch (e) {
      console.error('Location search failed', e);
      Sentry.captureException(e);

      await new Promise((res) => setTimeout(res, SPINNER_DURATION));
      setSearchStatus('error');

      throw e;
    }
  };

  const lookupAddress = async (addressLookup: AddressLookup) => {
    if (!meta) {
      return Promise.reject(new Error('Missing app config'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'X-CSRFToken': Cookies.get('csrftoken'),
    };

    setLookupStatus('sending');

    try {
      const params = normalizeLookupParams(addressLookup);
      const response = await httpClient.get<LookupAddressResponse>(meta.api.lookupAddress, {
        headers,
        params,
      });

      await new Promise((res) => setTimeout(res, SPINNER_DURATION));
      setLookupStatus('done');

      // Backend: { location: {...} }
      return response.data.location;
    } catch (e) {
      console.error('Location lookup failed', e);
      Sentry.captureException(e);

      await new Promise((res) => setTimeout(res, SPINNER_DURATION));
      setLookupStatus('error');

      throw e;
    }
  };

  return {
    searchStatus,
    searchAddress,
    lookupStatus,
    lookupAddress,
  };
};
